import Head from 'next/head'
import Link from 'next/link'
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'
import Divider from '@mui/material/Divider'
import Chip from '@mui/material/Chip'
import Breadcrumbs from '@mui/material/Breadcrumbs'
import { MDXRemote } from 'next-mdx-remote'
import { serialize } from 'next-mdx-remote/serialize'
import remarkGfm from 'remark-gfm'
import { getAllGuideSlugs, getGuideBySlug, getAllGuides } from 'src/utils/guides'
import { SITIO } from 'src/data/general/seo'
import AdSlot from 'src/@core/components/ads/AdSlot'

// Mismo escape que SeoHead: un '<' dentro del JSON cerraria el <script> antes de tiempo
const jsonLd = obj => JSON.stringify(obj).replace(/</g, '\\u003c')

const categoryColor = {
  Beginner: 'success',
  Strategy: 'primary',
  Reference: 'default',
  Advanced: 'warning'
}

// MDX components — maps markdown elements to MUI
const mdxComponents = {
  /*
    component explicito en los tres. Typography deriva la etiqueta del variant,
    asi que un `##` del markdown salia como <h5> justo despues del <h1> del
    titulo: un salto de dos niveles que rompe el orden de encabezados. El tamano
    visual no cambia, solo la etiqueta.
  */
  h1: props => <Typography variant='h4' component='h2' color='primary' gutterBottom {...props} />,
  h2: props => <Typography variant='h5' component='h2' color='primary' gutterBottom sx={{ mt: 3 }} {...props} />,
  h3: props => <Typography variant='h6' component='h3' color='primary' gutterBottom sx={{ mt: 2 }} {...props} />,
  h4: props => (
    <Typography variant='subtitle1' component='h3' fontWeight='bold' gutterBottom sx={{ mt: 2 }} {...props} />
  ),
  p: props => <Typography variant='body1' paragraph {...props} />,
  li: props => (
    <li>
      <Typography variant='body1' component='span' {...props} />
    </li>
  ),
  ul: props => <Box component='ul' sx={{ pl: 3, mb: 2 }} {...props} />,
  ol: props => <Box component='ol' sx={{ pl: 3, mb: 2 }} {...props} />,
  strong: props => <strong {...props} />,
  em: props => <em {...props} />,
  hr: () => <Divider sx={{ my: 3 }} />,
  /*
    La tabla va dentro de un contenedor con overflow-x propio: una tabla de 5
    columnas a 390 px de ancho no cabe, y sin este envoltorio el scroll horizontal
    se lo come el <body>, que es el defecto que la guia de AdSense señala en
    "Are all the navigational elements lined up correctly across all devices?".
  */
  table: props => (
    <Box sx={{ width: '100%', overflowX: 'auto', mb: 3 }}>
      <Box component='table' sx={{ borderCollapse: 'collapse', minWidth: '100%' }} {...props} />
    </Box>
  ),
  th: props => (
    <Box
      component='th'
      scope='col'
      sx={{
        textAlign: 'left',
        whiteSpace: 'nowrap',
        p: '8px 12px',
        borderBottom: '2px solid',
        borderColor: 'divider',
        fontSize: '0.875rem',
        fontWeight: 600
      }}
      {...props}
    />
  ),
  td: props => (
    <Box
      component='td'
      sx={{
        p: '8px 12px',
        borderBottom: '1px solid',
        borderColor: 'divider',
        fontSize: '0.875rem',
        fontVariantNumeric: 'tabular-nums',
        whiteSpace: 'nowrap'
      }}
      {...props}
    />
  ),
  blockquote: props => (
    <Box
      component='blockquote'
      sx={{
        borderLeft: '4px solid',
        borderColor: 'primary.main',
        pl: 2,
        py: 0.5,
        my: 2,
        color: 'text.secondary',
        fontStyle: 'italic'
      }}
      {...props}
    />
  ),
  a: ({ href, children }) => (
    <Link href={href} style={{ color: 'inherit' }}>
      {children}
    </Link>
  )
}

export async function getStaticPaths() {
  const slugs = getAllGuideSlugs()

  return {
    paths: slugs.map(slug => ({ params: { slug } })),
    fallback: false
  }
}

export async function getStaticProps({ params }) {
  const guide = getGuideBySlug(params.slug)
  if (!guide) return { notFound: true }

  /*
    remarkGfm es obligatorio, no un extra: sin el, las tablas de markdown no se
    parsean y salen al HTML como texto literal — la fila separadora |---|---|
    incluida. Dos de las guias son en su mayor parte tablas de datos, asi que sin
    esto se publicaban con aspecto de plantilla a medio renderizar.
  */
  const mdxSource = await serialize(guide.content, {
    mdxOptions: { format: 'md', remarkPlugins: [remarkGfm] }
  })

  // Related guides: same category, excluding current
  const allGuides = getAllGuides()
  const related = allGuides.filter(g => g.slug !== params.slug && g.category === guide.category).slice(0, 3)

  return {
    props: {
      guide: {
        slug: guide.slug,
        title: guide.title,
        description: guide.description,
        publishedAt: guide.publishedAt,
        updatedAt: guide.updatedAt,
        author: guide.author,
        category: guide.category,
        tags: guide.tags,
        coverImage: guide.coverImage,
        youtube: guide.youtube,
        tools: guide.tools,
        readingTime: guide.readingTime
      },
      mdxSource,
      related
    }
  }
}

const GuideArticle = ({ guide, mdxSource, related }) => {
  const publishDate = new Date(guide.publishedAt).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })

  const updateDate = new Date(guide.updatedAt).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })

  const migasJsonLd = jsonLd({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://waotools.com/' },
      { '@type': 'ListItem', position: 2, name: 'Guides', item: 'https://waotools.com/guides/' },
      // Sin la categoria: no tiene URL propia, y Google exige `item` en todo ListItem
      // salvo el ultimo. Con ella, Search Console marcaba 'Missing field "item"'.
      { '@type': 'ListItem', position: 3, name: guide.title, item: `https://waotools.com/guides/${guide.slug}/` }
    ]
  })

  // JSON-LD generated from trusted internal frontmatter — not user input
  const jsonLdString = JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: guide.title,
    description: guide.description,
    datePublished: guide.publishedAt,
    dateModified: guide.updatedAt,
    image: guide.coverImage ? `https://waotools.com${guide.coverImage}` : undefined,
    author: { '@type': 'Person', name: guide.author },
    publisher: {
      '@type': 'Organization',
      name: 'WAO Tools',
      url: 'https://waotools.com'
    },
    mainEntityOfPage: `https://waotools.com/guides/${guide.slug}/`
  })

  return (
    <>
      <Head>
        <title>{`${guide.title} | War and Order`}</title>
        <meta name='description' content={guide.description} />
        <meta property='og:title' content={`${guide.title} | War and Order`} />
        <meta property='og:description' content={guide.description} />
        <meta property='og:type' content='article' />
        <meta property='og:url' content={`https://waotools.com/guides/${guide.slug}/`} />
        {/*
          Las guias no emitian og:image, asi que al compartirlas no habia
          previsualizacion: ni el titulo se acompañaba de nada. Con la tarjeta
          declarada y el card en 'summary_large_image' el enlace ocupa el ancho
          completo en X, WhatsApp, Discord y Telegram.
        */}
        <meta property='og:image' content={SITIO.tarjeta} />
        <meta property='og:image:width' content={String(SITIO.tarjetaAncho)} />
        <meta property='og:image:height' content={String(SITIO.tarjetaAlto)} />
        <meta property='og:image:alt' content={`${guide.title} — ${SITIO.nombre}`} />
        <meta property='og:site_name' content={SITIO.nombre} />
        <meta property='article:published_time' content={guide.publishedAt} />
        <meta property='article:modified_time' content={guide.updatedAt} />
        <meta property='article:author' content={guide.author} />
        <meta name='twitter:card' content='summary_large_image' />
        <meta name='twitter:site' content={SITIO.twitter} />
        <meta name='twitter:image' content={SITIO.tarjeta} />
        <meta name='twitter:title' content={`${guide.title} | War and Order`} />
        <meta name='twitter:description' content={guide.description} />
        <link rel='canonical' href={`https://waotools.com/guides/${guide.slug}/`} />
        {/* JSON-LD: built from internal data, not user input */}
        <script type='application/ld+json'>{jsonLdString}</script>
        {/*
          BreadcrumbList. Las 20 paginas de herramienta lo emiten desde SeoHead, pero
          las guias no pasan por ahi —tienen <Head> propio— asi que eran las unicas 9
          sin el. Google usa esto para dibujar la ruta debajo del titulo en los
          resultados; sin el, una guia sale con la URL cruda.

          Las migas que ya se pintan en la pagina son estas mismas, en el mismo orden:
          el markup tiene que reflejar lo que se ve, o es motivo de accion manual.
        */}
        <script type='application/ld+json'>{migasJsonLd}</script>
      </Head>

      <Grid container spacing={6}>
        <Grid size={{ xs: 12 }}>
          {/* Breadcrumbs */}
          <Breadcrumbs sx={{ mb: 2 }}>
            <Link href='/' style={{ color: 'inherit', textDecoration: 'none' }}>
              Home
            </Link>
            <Link href='/guides/' style={{ color: 'inherit', textDecoration: 'none' }}>
              Guides
            </Link>
            {/* Texto y no enlace: /guides/ no filtra por categoria, y ?category= solo
                generaba URLs duplicadas de la misma lista. */}
            <Typography color='inherit'>{guide.category}</Typography>
            <Typography color='text.primary'>{guide.title}</Typography>
          </Breadcrumbs>

          <Card
            sx={{
              padding: '2rem',
              borderRadius: '12px',
              boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
              borderTop: '6px solid',
              borderTopColor: 'primary.main'
            }}
          >
            <Box component='article'>
              <Box component='header' sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                  <Chip label={guide.category} size='small' color={categoryColor[guide.category] || 'default'} />
                  {guide.tags?.map(tag => (
                    <Chip key={tag} label={tag} size='small' variant='outlined' />
                  ))}
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  {guide.coverImage && (
                    /*
                      width y height explicitos, y un contenedor con el mismo
                      tamano: son iconos del juego de 1-5 KB, asi que se muestran a
                      56 px en vez de a ancho completo, y el hueco queda reservado
                      antes de que la imagen llegue. Sin eso el titulo salta.
                    */
                    <Box
                      component='img'
                      src={guide.coverImage}
                      alt=''
                      width={56}
                      height={56}
                      loading='eager'
                      sx={{ width: 56, height: 56, objectFit: 'contain', flexShrink: 0 }}
                    />
                  )}
                  <Typography variant='h3' component='h1' color='primary' gutterBottom sx={{ mb: 0 }}>
                    {guide.title}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', color: 'text.secondary' }}>
                  <Typography variant='caption'>
                    By <strong>{guide.author}</strong>
                  </Typography>
                  <Typography variant='caption'>
                    Published: <time dateTime={guide.publishedAt}>{publishDate}</time>
                  </Typography>
                  {guide.updatedAt !== guide.publishedAt && (
                    <Typography variant='caption'>
                      Updated: <time dateTime={guide.updatedAt}>{updateDate}</time>
                    </Typography>
                  )}
                  <Typography variant='caption'>{guide.readingTime}</Typography>
                </Box>
              </Box>

              <Divider sx={{ mb: 3 }} />

              <Box sx={{ '& a': { color: 'primary.main' } }}>
                <MDXRemote {...mdxSource} components={mdxComponents} />
              </Box>

              {/*
                Anuncio al cierre del articulo. Las guias son las unicas paginas del
                sitio con texto largo, que es donde AdSense paga mejor y donde entra
                el trafico de busqueda; y al final del cuerpo no interrumpe la
                lectura ni compite con el video o con los enlaces a herramientas.

                Sin unidad configurada en src/data/general/ads.js no renderiza nada.
              */}
              <AdSlot slot='guiaArticulo' altoMinimo={250} />

              {/*
                El video va en un iframe y no en react-player: react-player arrastra
                hls.js y mux (~496 KB) y necesita JS para pintar nada. Un iframe con
                loading lazy no cuesta bundle y existe en el HTML estatico.

                El contenedor lleva la proporcion 16:9 con padding-top, asi que el
                hueco esta reservado desde el primer render y el contenido de abajo
                no se desplaza cuando el iframe carga.
              */}
              {guide.youtube && (
                <Box sx={{ mt: 4 }}>
                  <Typography variant='h5' component='h2' color='primary' gutterBottom>
                    Watch it
                  </Typography>
                  <Box
                    sx={{
                      position: 'relative',
                      width: '100%',
                      paddingTop: '56.25%',
                      borderRadius: '8px',
                      overflow: 'hidden'
                    }}
                  >
                    <Box
                      component='iframe'
                      src={guide.youtube}
                      title={`${guide.title} — video`}
                      loading='lazy'
                      allow='accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
                      allowFullScreen
                      sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }}
                    />
                  </Box>
                </Box>
              )}

              {/*
                Enlaces del articulo hacia las herramientas que menciona. El bloque
                de cada herramienta ya enlaza hacia las guias; esto cierra el otro
                sentido, que es lo que convierte 45 paginas colgadas de un menu en
                un sitio con caminos entre ellas.
              */}
              {guide.tools?.length > 0 && (
                <Box sx={{ mt: 4 }}>
                  <Divider sx={{ mb: 2 }} />
                  <Typography variant='subtitle2' component='h2' gutterBottom>
                    Tools used in this guide
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {guide.tools.map(t => (
                      <Chip
                        key={t}
                        component={Link}
                        href={t}
                        clickable
                        size='small'
                        variant='outlined'
                        label={t.replace(/\//g, '')}
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          </Card>
        </Grid>

        {/* Related Guides */}
        {related.length > 0 && (
          <Grid size={{ xs: 12 }}>
            <Typography variant='h5' component='h2' color='primary' gutterBottom>
              Related Guides
            </Typography>
            <Grid container spacing={4}>
              {related.map(r => (
                <Grid size={{ xs: 12, sm: 4 }} key={r.slug}>
                  <Card
                    component={Link}
                    href={`/guides/${r.slug}/`}
                    sx={{
                      display: 'block',
                      textDecoration: 'none',
                      p: 2,
                      borderRadius: '8px',
                      transition: 'transform 0.15s',
                      '&:hover': { transform: 'translateY(-2px)' }
                    }}
                  >
                    <Chip
                      label={r.category}
                      size='small'
                      color={categoryColor[r.category] || 'default'}
                      sx={{ mb: 1 }}
                    />
                    <Typography variant='subtitle1' component='h3' color='primary' gutterBottom>
                      {r.title}
                    </Typography>
                    <Typography variant='body2' color='text.secondary'>
                      {r.description}
                    </Typography>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Grid>
        )}
      </Grid>
    </>
  )
}

GuideArticle.noLoadingGate = true

export default GuideArticle
